import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { CHANNELS } from '../../lib/channels.js';
import { Button } from '../../components/Button.jsx';

const DIRECTIONS = [
  { value: 'surface',     label: 'Surface' },
  { value: 'reverse',     label: 'Reverse' },
  { value: 'cbw_surface', label: 'CBW surface' },
];

const num = (v) => Number.isFinite(parseInt(v, 10)) ? parseInt(v, 10) : 0;

/**
 * Inline form for adding or editing a mix.
 * - Selecting a printer rebuilds the recipe inputs from that printer's channels.
 * - Direction is a segmented control.
 * - Recipe values are integers 0–100.
 */
export const MixForm = ({ inkId, printers, existingMix, onSaved, onCancel }) => {
  const editing = !!existingMix;
  const [printerId, setPrinterId] = useState(existingMix?.printerId ?? printers[0]?.id ?? null);
  const [direction, setDirection] = useState(existingMix?.printDirection ?? 'surface');
  const [recipe, setRecipe] = useState(existingMix?.recipe ?? {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const printer = printers.find((p) => p.id === printerId);
  const channels = printer?.channels ?? [];

  // When printer changes (creating mode), reset recipe shape to that printer's channels at 0.
  useEffect(() => {
    if (editing) return;
    if (!printer) return;
    setRecipe(Object.fromEntries(printer.channels.map((c) => [c, 0])));
  }, [printerId, editing, printer]);

  const setChannel = (code, value) => {
    setRecipe((r) => ({ ...r, [code]: Math.max(0, Math.min(100, num(value))) }));
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // Only keep channels the printer actually has, in case of leftover state.
      const cleanRecipe = Object.fromEntries(
        channels.filter((c) => c in recipe).map((c) => [c, num(recipe[c])]),
      );
      const payload = { printerId, printDirection: direction, recipe: cleanRecipe };
      const saved = editing
        ? await api.patch(`/ink-books/mixes/${existingMix.id}`, payload)
        : await api.post(`/ink-books/inks/${inkId}/mixes`, payload);
      onSaved?.(saved);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="rounded-lg border border-border-strong bg-bg-card p-4">
      <div class="mb-3 flex items-center justify-between">
        <p class="m-0 font-serif text-[16px] font-medium text-text-primary">
          {editing ? 'Edit mix' : 'Add mix'}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">Printer</p>
          <select
            value={printerId ?? ''}
            disabled={editing}
            onChange={(e) => setPrinterId(Number(e.currentTarget.value))}
            class="block w-full rounded-md border border-border-strong bg-bg-base px-3 py-2 text-sm font-medium text-text-primary disabled:opacity-50 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          >
            {printers.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">Print direction</p>
          <div class="flex overflow-hidden rounded-md border border-border-strong">
            {DIRECTIONS.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => setDirection(d.value)}
                disabled={editing && d.value !== direction && existingMix?.printDirection !== d.value}
                class={`flex-1 px-2 py-2 text-[12px] font-semibold ${direction === d.value ? 'bg-brand text-[#1A0E04]' : 'bg-transparent text-text-tertiary hover:bg-bg-base disabled:cursor-not-allowed disabled:opacity-50'}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div class="mt-4">
        <p class="mb-1.5 text-[12px] font-semibold text-text-tertiary">
          Recipe · {printer?.name} channels
        </p>
        <div class="grid grid-cols-4 gap-2 sm:grid-cols-8">
          {channels.map((code) => {
            const ch = CHANNELS[code];
            if (!ch) return null;
            return (
              <div key={code}>
                <label class="mb-1 block text-center text-[10px] font-bold" style={{ color: ch.color }}>
                  {code.toUpperCase()}
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={recipe[code] ?? 0}
                  onInput={(e) => setChannel(code, e.currentTarget.value)}
                  class="block w-full rounded-md border border-border-strong bg-bg-base px-1.5 py-1.5 text-center font-serif text-[16px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
            );
          })}
        </div>
        {printer?.limitations && (
          <p class="mt-2 text-[11px] font-semibold text-ink-yellow">
            ⚠ {printer.name}: {printer.limitations}
          </p>
        )}
      </div>

      {error && (
        <p class="mt-3 text-[13px] font-semibold text-ink-magenta">
          {error instanceof ApiError ? error.message : (error.message ?? 'Failed to save')}
        </p>
      )}

      <div class="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={busy || !printerId}>
          {busy ? 'Saving…' : editing ? 'Update mix' : 'Add mix'}
        </Button>
      </div>
    </div>
  );
};
